/**
 * S06b — ONE LANGUAGE (Design DNA)
 *
 * Hero: HF#9 altarpiece dish + HF#4 neon kitchen as layered backdrops.
 * On top, a 5-panel "Come and See" grid stagger-reveals — each panel is
 * a moodboard image in a different duotone tint, showing the same visual
 * DNA applied across film, poster, product, merch, digital.
 *
 * Restored from pre-narrative-refactor (commit 568a262^) and re-inserted
 * after S06 Food Noir to break up the text-heavy spine with a design beat.
 */

import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MonumentType } from "@/components/primitives/MonumentType";
import { sectionCopy } from "@/lib/voice";
import { keyImages, moodboardUrl } from "@/lib/moodboard";
import dishHeroSrc from "@/assets/higgsfield/hf-09-dish-hero.webp";
import dishPlatedSrc from "@/assets/higgsfield/hf-04-dish-plated.webp";

gsap.registerPlugin(ScrollTrigger);

const panels = [
  { idx: keyImages.satriales, color: "#E86A1D", label: "FILM / 01" }, // flame
  { idx: keyImages.alchemistEditorial, color: "#5B8B5A", label: "POSTER / 02" }, // loden
  { idx: keyImages.trainspotting, color: "#B83319", label: "PRODUCT / 03" }, // blood
  { idx: keyImages.crucifixPCB, color: "#2E5C8A", label: "MERCH / 04" }, // cobalt
  { idx: keyImages.flameHead, color: "#F2C14E", label: "DIGITAL / 05" }, // gold
];

export function S06b_OneLanguage() {
  const copy = sectionCopy.s06b;
  const sectionRef = useRef<HTMLElement | null>(null);
  const panelRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        panelRefs.current.filter(Boolean),
        { scaleY: 0, transformOrigin: "bottom center" },
        {
          scaleY: 1,
          duration: 1,
          stagger: 0.18,
          ease: "power3.inOut",
          scrollTrigger: {
            trigger: el,
            start: "top 60%",
            toggleActions: "play none none reverse",
          },
        },
      );
    }, el);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="s06b"
      className="section"
      style={{
        backgroundColor: "var(--ink)",
        color: "var(--bone)",
        flexDirection: "column",
        padding: "10vh var(--space-lg)",
      }}
    >
      {/* Dish hero background — HF#9 altarpiece dish */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${dishHeroSrc})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "brightness(0.45) contrast(1.15) saturate(0.88)",
          zIndex: 0,
        }}
      />
      {/* Secondary atmospheric layer — HF#4 neon kitchen */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${dishPlatedSrc})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "brightness(0.3)",
          mixBlendMode: "multiply",
          opacity: 0.35,
          zIndex: 0,
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(5,5,5,0.85) 0%, rgba(5,5,5,0.55) 40%, rgba(5,5,5,0.85) 100%)",
          zIndex: 1,
        }}
      />

      <MonumentType word={copy.monument} theme="ink" />

      {/* Content */}
      <div
        style={{
          position: "relative",
          zIndex: 3,
          textAlign: "center",
          marginBottom: "var(--space-xl)",
        }}
      >
        <div
          className="type-meta-sm"
          style={{
            color: "var(--fog)",
            marginBottom: "var(--space-md)",
            letterSpacing: "var(--ls-meta-wide)",
          }}
        >
          {copy.meta} — THE DNA
        </div>
        <h2
          className="type-hero-lg"
          style={{ color: "var(--bone)", margin: 0 }}
        >
          {copy.hero}
        </h2>
        {copy.subhero && (
          <p
            className="type-voice"
            style={{
              color: "var(--fog)",
              marginTop: "var(--space-md)",
              fontSize: "clamp(1.25rem, 1.6vw, 1.75rem)",
            }}
          >
            {copy.subhero}
          </p>
        )}
      </div>

      {/* 5-panel Come and See grid */}
      <div
        style={{
          position: "relative",
          zIndex: 3,
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: "var(--space-sm)",
          width: "90vw",
          height: "48vh",
        }}
      >
        {panels.map((panel, i) => (
          <div
            key={panel.idx}
            ref={(el) => {
              panelRefs.current[i] = el;
            }}
            style={{
              position: "relative",
              overflow: "hidden",
              backgroundColor: "var(--oil)",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: `url(${moodboardUrl(panel.idx)})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: "grayscale(100%) contrast(1.3) brightness(0.75)",
              }}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: panel.color,
                mixBlendMode: "color",
                opacity: 0.72,
              }}
            />
            <span
              className="type-meta-sm"
              style={{
                position: "absolute",
                bottom: "var(--space-sm)",
                left: "var(--space-sm)",
                color: "var(--bone)",
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                textShadow: "0 2px 8px rgba(0,0,0,0.8)",
                letterSpacing: "0.3em",
              }}
            >
              {panel.label}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
